import React, { useState, useEffect } from 'react';
import {
  Card, Button, Modal, Form, Input, message, List, Popconfirm, Space, Tag, Tooltip
} from 'antd';
import {
  SaveOutlined, FolderOpenOutlined, DeleteOutlined, EditOutlined, 
  PlusOutlined, HistoryOutlined
} from '@ant-design/icons';
import { simulationAPI } from '../services/api';
// Using built-in Date API instead of moment

const TestCaseManager = ({ 
  currentConfig, 
  currentPoolDistribution, 
  currentMonteCarloResults,
  onLoadTestCase 
}) => {
  const [testCases, setTestCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveModalVisible, setSaveModalVisible] = useState(false);
  const [saveForm] = Form.useForm();

  useEffect(() => {
    loadTestCases();
  }, []);

  const loadTestCases = async () => {
    try {
      setLoading(true);
      const response = await simulationAPI.listTestCases();
      setTestCases(response.test_cases || []);
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTestCase = async (values) => {
    try {
      await simulationAPI.createTestCase({
        name: values.name,
        description: values.description,
        config: currentConfig,
        pool_distribution: currentPoolDistribution,
        monte_carlo_results: currentMonteCarloResults
      });
      message.success('Test case saved successfully');
      setSaveModalVisible(false);
      saveForm.resetFields();
      loadTestCases();
    } catch (error) {
      message.error(error.message);
    }
  };

  const handleLoadTestCase = async (testCase) => {
    try {
      if (onLoadTestCase) {
        await onLoadTestCase(testCase);
        message.success(`Loaded test case: ${testCase.name}`);
      }
    } catch (error) {
      message.error(error.message);
    }
  };

  const handleDeleteTestCase = async (testId) => {
    try {
      await simulationAPI.deleteTestCase(testId);
      message.success('Test case deleted successfully');
      loadTestCases();
    } catch (error) {
      message.error(error.message);
    }
  };

  const canSave = currentConfig && (currentMonteCarloResults || currentPoolDistribution);

  return (
    <Card
      title={
        <span>
          <FolderOpenOutlined style={{ marginRight: 8 }} />
          Test Cases Manager
        </span>
      }
      extra={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setSaveModalVisible(true)}
          disabled={!canSave}
        >
          Save Current
        </Button>
      }
    >
      <List
        loading={loading}
        dataSource={testCases}
        renderItem={(testCase) => (
          <List.Item
            actions={[
              <Tooltip title="Load test case">
                <Button
                  type="text"
                  icon={<HistoryOutlined />}
                  onClick={() => handleLoadTestCase(testCase)}
                />
              </Tooltip>,
              <Popconfirm
                title="Are you sure you want to delete this test case?"
                onConfirm={() => handleDeleteTestCase(testCase.id)}
                okText="Yes"
                cancelText="No"
              >
                <Tooltip title="Delete test case">
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                  />
                </Tooltip>
              </Popconfirm>
            ]}
          >
            <List.Item.Meta
              title={
                <Space>
                  {testCase.name}
                  {testCase.monte_carlo_results && (
                    <Tag color="green" icon={<HistoryOutlined />}>
                      MC Results
                    </Tag>
                  )}
                </Space>
              }
              description={
                <div>
                  <div>{testCase.description || 'No description'}</div>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                    Created: {new Date(testCase.created_at).toLocaleString('ru-RU')}
                    {testCase.updated_at !== testCase.created_at && (
                      <span> • Updated: {new Date(testCase.updated_at).toLocaleString('ru-RU')}</span>
                    )}
                  </div>
                </div>
              }
            />
          </List.Item>
        )}
        locale={{ emptyText: 'No test cases saved yet' }}
      />

      <Modal
        title="Save Test Case"
        open={saveModalVisible}
        onCancel={() => {
          setSaveModalVisible(false);
          saveForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={saveForm}
          layout="vertical"
          onFinish={handleSaveTestCase}
        >
          <Form.Item
            name="name"
            label="Test Case Name"
            rules={[{ required: true, message: 'Please enter a name' }]}
          >
            <Input placeholder="e.g., Low Risky Pool Balance" />
          </Form.Item>
          
          <Form.Item
            name="description"
            label="Description"
          >
            <Input.TextArea 
              placeholder="Describe what this test case represents..."
              rows={3}
            />
          </Form.Item>

          <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
            <strong>Will save:</strong>
            <ul style={{ margin: '8px 0 0 0' }}>
              <li>Current simulation configuration</li>
              {currentPoolDistribution && <li>Pool distribution settings</li>}
              {currentMonteCarloResults && <li>Monte Carlo results ({currentMonteCarloResults.summary?.samples} samples)</li>}
            </ul>
          </div>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
                Save Test Case
              </Button>
              <Button onClick={() => setSaveModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default TestCaseManager;
